import { useQuery, useZero } from "@rocicorp/zero/react";
import { nanoid } from "nanoid";
import {
  forwardRef,
  useCallback,
  useEffect,
  useEffectEvent,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import type { EffortLevel } from "../../../shared/aiProviders";
import type { ChatEntryWithSubTables } from "../../../shared/aiSdkMessages";
import { CLIENT_AUTO_RESOLVED_TOOLS } from "../../../shared/clientAutoResolvedTools";
import { queries } from "../../../shared/queries";
import useChatScroll from "../../hooks/useChatScroll";
import { useChatHistoryScroll } from "../../hooks/useChatHistory";
import { HistoryLoading } from "./HistoryLoading";
import { useExploratoryHandoff } from "./useExploratoryHandoff";
import { useChatSounds } from "../../hooks/useChatSounds";
import { useCreateSkill } from "../../hooks/useCreateSkill";
import { useFavicon } from "../../hooks/useFavicon";
import { useFileDropzone } from "../../hooks/useFileDropzone";
import { useScopedModel } from "../../hooks/useScopedModel";
import { useFocusShortcut } from "../../hooks/useFocusShortcut";
import { useIsMobile } from "../../hooks/useIsMobile";
import { usePlanModeShortcut } from "../../hooks/usePlanModeShortcut";
import { useMentions } from "../../hooks/useMentions";
import { useSlashSkills } from "../../hooks/useSlashSkills";
import {
  type StreamMessage,
  useStreamingText,
} from "../../hooks/useStreamingText";
import { cacheToolOutput } from "../../hooks/useToolOutput";
import { useWorkflowChangeReview } from "../../hooks/useWorkflowChangeReview";
import { toAttachmentPayload } from "../../lib/attachments";
import { maybeDisplayBrowserNotification } from "../../lib/browserNotifications";
import { getStatusState } from "../../lib/chatStatus";
import { restoreDraft, useCaretToEndOnMount, useDraft } from "../../lib/draft";
import {
  loadEffort,
  resolveEffortFromEntries,
  saveEffort,
} from "../../lib/effort";
import { formatElapsedTime } from "../../lib/formatElapsedTime";
import {
  type BashFileTarget,
  inputToFileEdit,
  inputToFileTarget,
  inputToFileWrite,
  type StreamingBashWrite,
  type StreamingFileEdit,
} from "../../lib/parseStreamingBashWrite";
import {
  loadPlanMode,
  resolveDefaultPlanMode,
  resolvePlanModeFromEntries,
  resolveSeededPlanMode,
  savePlanMode,
} from "../../lib/planMode";
import { playChatNeedsInput, playClick } from "../../lib/sounds";
import { AttachmentHoverPreview } from "../AttachmentHoverPreview";
import {
  AttachmentPreview,
  AttachmentPreviewRow,
  AttachmentRowAnimator,
  attachmentFileIcon,
  Dropzone,
  marchingDashes,
} from "../AttachmentPreviews";
import { Button } from "../Button";
import { ChatBanner } from "../ChatBanner";
import { FlyingDot } from "../FlyingDot";
import { Icon } from "../Icon";
import { PlanModeToggle } from "../PlanModeToggle";
import { Spinner } from "../Spinner";
import { StatusIndicator } from "../StatusIndicator";
import { Text } from "../Text";
import { Textarea } from "../Textarea";
import { Tooltip } from "../Tooltip";
import { VoiceModeButton } from "../VoiceModeButton";
import { VoiceRecorder } from "../VoiceRecorder";
import { ChatMessageNav } from "./ChatMessageNav";
import { resolvePromptResult } from "./ChatTool";
import { ComposerStrip } from "./ComposerStrip";
import { ComposerStripToggle } from "./ComposerStripToggle";
import { FollowNudge } from "./FollowNudge";
import { ConnectorLogo } from "../ConnectorLogo";
import { AccentBar } from "./MessageContent";
import { SlashMenu, type SuggestionSet } from "./SlashMenu";
import { MentionCompletion } from "./MentionCompletion";
import { SlashSkillCompletion } from "./SlashSkillCompletion";
import { QueueStack } from "./QueueStack";
import { claimQueued, type QueuedMessage } from "./queueStorage";
import { useChatQueue } from "./useChatQueue";
import { navigate, routes, useCurrentLocation } from "../../lib/urls";

import {
  buildSendPayload,
  displaySnippetPath,
  hasSendableContent,
} from "./buildSendPayload";
import {
  canInterrupt,
  hasPlanModeHistory as entriesHavePlanModeHistory,
  isGroupChat,
  latestPendingToolUse,
  latestToolMessage,
  latestToolStep,
  latestUserMessageBefore,
} from "./chatEntries";
import type {
  ChatHandle,
  ChatProps,
  ChatSendPayload,
  PreviousConversation,
} from "./chatTypes";
import {
  ConversationViewport,
  FlowingLiveConversation,
} from "./ConversationViewport";
import { groupChatEntries } from "./groupChatEntries";
import { Transcript } from "./Transcript";
import {
  TranscriptProvider,
  useTranscriptExpansionState,
} from "./TranscriptContext";

import {
  activeStates,
  StatusLabel,
  StatusTimer,
  statusText,
} from "./ChatStatus";
import {
  ChatForm,
  ComposerControlGroup,
  ComposerControls,
  ComposerInputWrap,
  Container,
  FollowAvatarButton,
  Messages,
  MessagesContent,
  NextCheckpointLink,
  PrevCheckpointLink,
  RecordingDot,
  RecordingIndicator,
  ScrollToBottomButton,
  StatusContainer,
  StopButton,
  TextareaContainer,
  VoiceRecorderContainer,
} from "./chatStyles";

const emptyEntries: readonly ChatProps["entries"][number][] = [];

function notifyChatDone(messages: ReadonlyMap<string, StreamMessage>): void {
  const text = [...messages.values()].findLast(
    (m) => m.role === "assistant" && m.text
  )?.text;
  if (!text) return;
  maybeDisplayBrowserNotification(
    "3B",
    text.replace(/\s+/g, " ").trim().slice(0, 200)
  );
}

export const Chat = forwardRef<ChatHandle, ChatProps>(function Chat(
  {
    entries: sourceEntries,
    onLoadOlder,
    loadingOlder,
    liveConversation,
    reviewVersion,
    previousConversation,
    exploratoryConversation,
    writeChatId,
    streamChatId = writeChatId,
    draftKey,
    onSend,
    onInterrupt,
    onProvideToolResult,
    prevCheckpointLink,
    prevCheckpointLabel,
    nextCheckpointLink,
    nextCheckpointLabel,
    isCurrentUserChat,
    canWrite,
    tools,
    suggestions,
    soundsEnabled,
    respondingAt,
    lastLoopHeartbeatAt,
    awaitingAction,
    pendingActionStatus,
    codeSnippets,
    removeCodeSnippet,
    clearCodeSnippets,
    elementSnippets,
    removeElementSnippet,
    clearElementSnippets,
    selectedStepName,
    spaceId,
    mentionSteps,
    mentionConnectors,
    mentionTenantConnectors,
    mentionConnectorCatalog,
    mentionConnectorContextLabel,
    composerContext,
    historyBanner,
    topOverlay,
    pendingPromptOverride,
    outerScroll = false,
    outerScrollRef,
    onStatusClick,
    followActive = false,
    showFollowNudge = false,
    onDismissFollowNudge,
    onStreamingToolStep,
    onStatusChange,
    onStreamingFileWrite,
    onStreamingFileEdit,
    onStreamingFileTarget,
    planModeEnabled = true,
    planModeDefaultSurface = "chat",
    resolveFileHref,
    EmptyState,
    placeholder = "Ask AI to build",
    modelScope,
    onZipFile,
    acceptAnyFile,
  },
  forwardedRef
) {
  const [departingConversation, setDepartingConversation] =
    useState<PreviousConversation | null>(null);
  const [livePanelVisible, setLivePanelVisible] = useState(true);
  const [liveConversationId, setLiveConversationId] = useState(
    liveConversation?.id
  );
  if (liveConversationId !== liveConversation?.id) {
    setLiveConversationId(liveConversation?.id);
    if (liveConversation) setDepartingConversation(null);
  }
  const entries =
    departingConversation && liveConversation ? emptyEntries : sourceEntries;
  const previousContext = exploratoryConversation
    ? undefined
    : departingConversation &&
        previousConversation?.id === departingConversation.id
      ? {
          ...departingConversation,
          entries: previousConversation.entries,
          onLoadOlder: previousConversation.onLoadOlder,
          loadingOlder: previousConversation.loadingOlder,
        }
      : (departingConversation ?? previousConversation);
  const canRefreshPreservedHistory =
    !!departingConversation &&
    !!exploratoryConversation &&
    departingConversation.id === exploratoryConversation.live.id &&
    departingConversation.reviewVersion?.commitId ===
      exploratoryConversation.live.reviewVersion?.commitId;
  const conversationToDisplay = canRefreshPreservedHistory
    ? {
        ...departingConversation,
        entries: exploratoryConversation.live.entries,
        onLoadOlder: exploratoryConversation.live.onLoadOlder,
        loadingOlder: exploratoryConversation.live.loadingOlder,
      }
    : (departingConversation ?? exploratoryConversation?.live);
  const chatEntries = entries as ChatEntryWithSubTables[];
  const isResponding = respondingAt != null;
  const serverHasStartedRun = lastLoopHeartbeatAt != null;
  const { messages: streamingEntries, runStatus } = useStreamingText(
    streamChatId,
    isResponding && serverHasStartedRun,
    notifyChatDone
  );

  const lastStreamingTool = useMemo(
    () => latestToolMessage(streamingEntries),
    [streamingEntries]
  );
  const streamingToolStep = useMemo(
    () => latestToolStep(streamingEntries),
    [streamingEntries]
  );
  useEffect(() => {
    onStreamingToolStep?.(streamingToolStep);
  }, [streamingToolStep, onStreamingToolStep]);

  const lastStreamingToolInput =
    (lastStreamingTool?.toolInput as Record<string, unknown> | undefined) ??
    null;

  const streamingFileWrite = useMemo<StreamingBashWrite | null>(
    () => inputToFileWrite(lastStreamingToolInput ?? undefined),
    [lastStreamingToolInput]
  );
  useEffect(() => {
    onStreamingFileWrite?.(streamingFileWrite);
  }, [streamingFileWrite, onStreamingFileWrite]);

  const streamingFileEdit = useMemo<StreamingFileEdit | null>(
    () => inputToFileEdit(lastStreamingToolInput ?? undefined),
    [lastStreamingToolInput]
  );
  useEffect(() => {
    onStreamingFileEdit?.(streamingFileEdit);
  }, [streamingFileEdit, onStreamingFileEdit]);

  const streamingFileTarget = useMemo<BashFileTarget | null>(
    () => inputToFileTarget(lastStreamingToolInput ?? undefined),
    [lastStreamingToolInput]
  );
  useEffect(() => {
    onStreamingFileTarget?.(streamingFileTarget);
  }, [streamingFileTarget, onStreamingFileTarget]);

  const z = useZero();
  const [preferences] = useQuery(queries.userPreferences());
  const followUpBehavior =
    preferences?.followUpBehavior === "steer" ? "steer" : "queue";
  const resolveForce = (modifier: boolean) =>
    followUpBehavior === "steer" ? !modifier : modifier;
  const defaultPlanMode =
    !planModeEnabled || planModeDefaultSurface === "none"
      ? false
      : resolveDefaultPlanMode(preferences, planModeDefaultSurface);
  const { pathname } = new URL(useCurrentLocation(), location.origin);
  const [input, setInput] = useDraft(`${pathname}:${draftKey}`);
  const historyPlanMode = useMemo(
    () => resolvePlanModeFromEntries(entries),
    [entries]
  );
  const hasPlanModeHistory = useMemo(
    () => entriesHavePlanModeHistory(chatEntries),
    [chatEntries]
  );
  const [planMode, setPlanMode] = useState(
    () =>
      planModeEnabled && (historyPlanMode || loadPlanMode(writeChatId) === true)
  );
  const planModeChatIdRef = useRef<string | null>(writeChatId);
  const planModeTouchedRef = useRef(false);
  useEffect(() => {
    const chatChanged = planModeChatIdRef.current !== writeChatId;
    if (chatChanged) planModeTouchedRef.current = false;
    planModeChatIdRef.current = writeChatId;
    if (chatChanged || !planModeTouchedRef.current) {
      setPlanMode(
        planModeEnabled &&
          resolveSeededPlanMode({
            hasHistory: hasPlanModeHistory,
            historyPlanMode,
            savedPlanMode: loadPlanMode(writeChatId),
            defaultPlanMode,
            surface: planModeDefaultSurface,
          })
      );
    }
  }, [
    writeChatId,
    historyPlanMode,
    hasPlanModeHistory,
    defaultPlanMode,
    planModeEnabled,
    planModeDefaultSurface,
  ]);
  const togglePlanMode = useCallback(() => {
    if (!planModeEnabled) return;
    planModeTouchedRef.current = true;
    setPlanMode((on) => {
      const next = !on;
      savePlanMode(writeChatId, next);
      return next;
    });
  }, [planModeEnabled, writeChatId]);
  usePlanModeShortcut(togglePlanMode, planModeEnabled);
  const historyEffort = useMemo(
    () => resolveEffortFromEntries(entries),
    [entries]
  );
  const [localEffort, setLocalEffort] = useState<EffortLevel | null>(() =>
    loadEffort(writeChatId)
  );
  const [effortChatId, setEffortChatId] = useState(writeChatId);
  if (effortChatId !== writeChatId) {
    setEffortChatId(writeChatId);
    setLocalEffort(loadEffort(writeChatId));
  }
  const effort = localEffort ?? historyEffort;
  const [chatModel, setChatModel] = useState<string | null>(null);
  const { providerId: modelProviderId, chatModel: effectiveChatModel } =
    useScopedModel(modelScope, {
      chatModel,
    });

  // Keeps the callbacks feeding the memoized Transcript identity-stable across
  // composer changes; readers are post-commit event handlers, so the
  // effect-time mirror is never stale for them.
  const composerDefaultsRef = useRef({ planMode, effort, effectiveChatModel });
  useEffect(() => {
    composerDefaultsRef.current = { planMode, effort, effectiveChatModel };
  });

  const [selectedQueueItem, setSelectedQueueItem] = useState<{
    index: number;
    button: "cancel" | "edit" | "send";
  } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [celebrateTrigger, setCelebrateTrigger] = useState<number | null>(null);
  const celebrateCountRef = useRef(0);
  useEffect(() => {
    const count =
      input.match(
        /\b(party|finally|magic|wow|genius|beautiful|actually\s+works)\b/gi
      )?.length ?? 0;
    if (count > celebrateCountRef.current) {
      setCelebrateTrigger(Date.now());
    }
    celebrateCountRef.current = count;
  }, [input]);

  const groupChat = useMemo(() => isGroupChat(chatEntries), [chatEntries]);

  const {
    pendingAttachments,
    isUploading,
    isDragging,
    flyingFiles,
    processFiles,
    removeAttachment,
    clearAttachments,
    removeFlyingFile,
    handlePaste,
  } = useFileDropzone({
    enabled: isCurrentUserChat && canWrite,
    providerId: modelProviderId,
    onZipFile,
    acceptAnyFile,
  });

  useEffect(() => {
    if (isDragging && soundsEnabled) playChatNeedsInput();
  }, [isDragging, soundsEnabled]);

  const activeChatEntries =
    writeChatId === null
      ? []
      : chatEntries.filter((entry) => entry.chatId === writeChatId);
  const statusState = getStatusState(
    isResponding ? chatEntries : activeChatEntries,
    isResponding,
    runStatus,
    awaitingAction,
    pendingActionStatus
  );
  const isActiveStatus = activeStates.includes(statusState);
  // An answer alone marks the chat idle, so queued messages wait for the turn its staged files start.
  const [answerFiles, setAnswerFiles] = useState<"sending" | "sent" | null>(
    null
  );
  if (answerFiles === "sent" && isActiveStatus) setAnswerFiles(null);
  const reviewRequest = useWorkflowChangeReview({
    commitId:
      isCurrentUserChat && !(departingConversation && liveConversation)
        ? (reviewVersion?.commitId ?? null)
        : null,
    workflowId: reviewVersion?.workflowId ?? "",
  });
  const changeReview = reviewRequest.review;
  const reviewing = changeReview?.status === "generating" && !isActiveStatus;
  const displayStatusState = reviewing ? "building" : statusState;
  const activityStartedAt = reviewing ? changeReview.createdAt : respondingAt;

  const [isTypewriterAnimating, setIsTypewriterAnimating] = useState(false);
  useChatSounds(displayStatusState, isTypewriterAnimating, soundsEnabled);
  useFavicon(
    displayStatusState,
    isTypewriterAnimating,
    modelScope.type === "personal" ? "chat" : "workflow"
  );
  useEffect(() => {
    onStatusChange?.(statusState);
  }, [statusState, onStatusChange]);

  const returningToLive = !!exploratoryConversation?.onCloseComplete;
  const [scrollContainerRef, resetScroll, isScrolledUp] = useChatScroll(
    outerScroll ? outerScrollRef : undefined,
    returningToLive
  );
  useChatHistoryScroll(scrollContainerRef, entries, onLoadOlder, loadingOlder);

  const exploring = !!exploratoryConversation?.banner;
  const [hasExplored, setHasExplored] = useState(exploring);
  const [wasExploring, setWasExploring] = useState(exploring);
  if (exploring && !hasExplored) setHasExplored(true);
  if (wasExploring !== exploring) {
    setWasExploring(exploring);
    if (wasExploring) setDepartingConversation(null);
  }
  const transcriptRef = useRef<HTMLElement>(null);
  useExploratoryHandoff(
    exploring,
    isScrolledUp,
    scrollContainerRef,
    transcriptRef
  );

  const messagesWrapperRef = useRef<HTMLDivElement | null>(null);
  const setComposerRef = useCallback(
    (form: HTMLFormElement | null) => {
      if (!form || !outerScroll) return;
      const container = form.parentElement!;
      const observer = new ResizeObserver(() => {
        container.style.setProperty(
          "--chat-composer-height",
          `${form.offsetHeight}px`
        );
      });
      observer.observe(form);
      return () => {
        observer.disconnect();
        container.style.removeProperty("--chat-composer-height");
      };
    },
    [outerScroll]
  );
  const [queueOverlayHeight, setQueueOverlayHeight] = useState(0);
  const queueOverlayObserverRef = useRef<ResizeObserver | null>(null);
  const setQueueOverlayRef = useCallback((el: HTMLDivElement | null) => {
    queueOverlayObserverRef.current?.disconnect();
    queueOverlayObserverRef.current = null;
    if (!el) {
      setQueueOverlayHeight(0);
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      setQueueOverlayHeight(entry?.contentRect.height ?? 0);
    });
    observer.observe(el);
    queueOverlayObserverRef.current = observer;
  }, []);
  const topSentinelRef = useRef<HTMLDivElement | null>(null);
  const [topVisible, setTopVisible] = useState(false);
  useEffect(() => {
    const sentinel = topSentinelRef.current;
    const root = scrollContainerRef.current;
    if (!sentinel || !root) return;
    const observer = new IntersectionObserver(
      ([entry]) => setTopVisible(entry?.isIntersecting ?? false),
      { root, threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [scrollContainerRef]);

  const bottomSentinelRef = useRef<HTMLDivElement | null>(null);
  const [bottomVisible, setBottomVisible] = useState(false);
  useEffect(() => {
    const sentinel = bottomSentinelRef.current;
    const root = scrollContainerRef.current;
    if (!sentinel || !root) return;
    const observer = new IntersectionObserver(
      ([entry]) => setBottomVisible(entry?.isIntersecting ?? false),
      { root, threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [scrollContainerRef]);

  const handleTypewriterAnimatingChange = useCallback((animating: boolean) => {
    setIsTypewriterAnimating(animating);
  }, []);
  const isMobile = useIsMobile();
  const textareaRef = useFocusShortcut<HTMLTextAreaElement>();
  useCaretToEndOnMount(textareaRef);
  const [isTextareaFocused, setIsTextareaFocused] = useState(true);
  const [showComposerFade, setShowComposerFade] = useState(false);
  const updateComposerFade = useCallback(() => {
    const el = textareaRef.current;
    setShowComposerFade(!!el && el.scrollHeight > el.clientHeight + 1);
  }, [textareaRef]);
  const scrollComposerToEnd = useCallback(() => {
    const el = textareaRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    updateComposerFade();
  }, [textareaRef, updateComposerFade]);

  const fillAnimationRef = useRef<number | null>(null);
  const fillInput = useCallback(
    (text: string) => {
      if (fillAnimationRef.current !== null) {
        cancelAnimationFrame(fillAnimationRef.current);
      }
      // Type into the composer at the same cadence as the space-list animated
      // placeholder, so a picked prompt animates in rather than snapping in.
      const charsPerSecond = 188;
      let startTime: number | null = null;
      const step = (time: number) => {
        if (startTime === null) startTime = time;
        const revealed = Math.min(
          text.length,
          Math.floor(((time - startTime) / 1000) * charsPerSecond)
        );
        setInput(text.slice(0, revealed));
        const el = textareaRef.current;
        if (el) {
          // Re-focus each frame: the picked card takes focus on pointerdown and
          // the empty state unmounts mid-animation, so the composer would
          // otherwise not be focused when the typing finishes.
          el.focus({ preventScroll: true });
          el.setSelectionRange(revealed, revealed);
        }
        scrollComposerToEnd();
        fillAnimationRef.current =
          revealed < text.length ? requestAnimationFrame(step) : null;
      };
      fillAnimationRef.current = requestAnimationFrame(step);
    },
    [textareaRef, scrollComposerToEnd, setInput]
  );

  useEffect(() => {
    return () => {
      if (fillAnimationRef.current !== null) {
        cancelAnimationFrame(fillAnimationRef.current);
      }
    };
  }, []);

  useEffect(() => {
    requestAnimationFrame(updateComposerFade);
  }, [updateComposerFade]);

  const snippetCount =
    (codeSnippets?.length ?? 0) + (elementSnippets?.length ?? 0);
  const prevSnippetCount = useRef(snippetCount);
  useEffect(() => {
    if (snippetCount > prevSnippetCount.current && !isMobile) {
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
    prevSnippetCount.current = snippetCount;
  }, [snippetCount, isMobile, textareaRef]);

  // Focus the composer when a fresh chat opens (e.g. the "+" new-chat button),
  // deferred so it wins the focus race against the button that was just pressed.
  const didFocusNewChat = useRef(false);
  useEffect(() => {
    if (didFocusNewChat.current) return;
    if (isMobile || !canWrite || entries.length > 0) return;
    didFocusNewChat.current = true;
    const raf = requestAnimationFrame(() => textareaRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [isMobile, canWrite, entries.length, textareaRef]);

  const [isRecording, setIsRecording] = useState(false);
  const [recordingStartTime, setRecordingStartTime] = useState<number | null>(
    null
  );
  const [recordingElapsedTime, setRecordingElapsedTime] = useState(0);
  const transcriptExpansion = useTranscriptExpansionState();
  const { expandedToolGroups, expandedUserActionGroups, expandedToolUse } =
    transcriptExpansion;
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { canCreate: canCreateSkill, createSkill } = useCreateSkill(spaceId);

  const {
    availableSkills,
    selectedSkills,
    addSkill,
    removeSkill,
    clearSkills,
    syncSlashToken,
    slashCompletion,
    handleSlashKeyDown,
  } = useSlashSkills({
    spaceId,
    textareaRef,
    value: input,
    setValue: setInput,
    onAfterAccept: scrollComposerToEnd,
    menuOpen: showSuggestions,
  });

  const {
    selectedSteps,
    removeStep,
    clearSteps,
    selectedConnectors,
    removeConnector,
    clearConnectors,
    syncMentionToken,
    mentionCompletion,
    handleMentionKeyDown,
  } = useMentions({
    textareaRef,
    value: input,
    setValue: setInput,
    steps: mentionSteps ?? [],
    connectors: mentionConnectors ?? [],
    tenantConnectors: mentionTenantConnectors ?? [],
    connectorCatalog: mentionConnectorCatalog ?? [],
    contextLabel: mentionConnectorContextLabel,
    onAfterAccept: scrollComposerToEnd,
  });

  const toolsByName = useMemo(
    () => new Map(tools.map((t) => [t.name, t])),
    [tools]
  );

  const pendingPromptTool = useMemo(() => {
    if (statusState !== "waiting" || writeChatId === null) return null;
    const lastTool = latestPendingToolUse(
      chatEntries,
      writeChatId,
      (toolName) => !!toolsByName.get(toolName)?.PendingPrompt
    );
    if (!lastTool?.toolCall) return null;
    const tool = toolsByName.get(lastTool.toolCall.toolName);
    if (!tool?.PendingPrompt) return null;
    return {
      toolCallId: lastTool.id,
      input: lastTool.toolCall.input,
      PendingPrompt: tool.PendingPrompt,
    };
  }, [chatEntries, statusState, toolsByName, writeChatId]);

  const scrollToBottom = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const distance =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distance < 4) {
      resetScroll();
      return;
    }
    container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    // no scrollend on iOS Safari; useChatScroll's scroll listener resets there
    container.addEventListener("scrollend", resetScroll, { once: true });
  };

  useEffect(() => {
    if (!recordingStartTime) return;
    const interval = setInterval(
      () => setRecordingElapsedTime(Date.now() - recordingStartTime),
      1000
    );
    return () => clearInterval(interval);
  }, [recordingStartTime]);

  const resetScrollOnMount = useEffectEvent(resetScroll);
  useEffect(() => resetScrollOnMount(), []);

  const groupedEntries = useMemo(
    () =>
      groupChatEntries(
        chatEntries,
        statusState,
        writeChatId,
        pendingPromptTool?.toolCallId
      ),
    [chatEntries, statusState, writeChatId, pendingPromptTool?.toolCallId]
  );

  const withSelectedChatModel = useCallback(
    (payload: ChatSendPayload): ChatSendPayload => ({
      ...payload,
      chatModel:
        payload.chatModel ??
        composerDefaultsRef.current.effectiveChatModel ??
        undefined,
    }),
    []
  );

  const send = useCallback(
    (payload: ChatSendPayload, restoreOnError?: string) => {
      const fromLive = liveConversation ?? exploratoryConversation?.live;
      if (
        fromLive &&
        !exploring &&
        sourceEntries.length > 0 &&
        !departingConversation
      ) {
        const scroll = scrollContainerRef.current;
        setDepartingConversation({
          ...fromLive,
          entries: sourceEntries,
          scrollTop: scroll?.scrollTop ?? 0,
          viewport: scroll
            ? {
                height: scroll.clientHeight,
                bottom: scroll.getBoundingClientRect().bottom,
              }
            : undefined,
          scrollBottom: scroll
            ? Math.max(
                0,
                scroll.scrollHeight - scroll.clientHeight - scroll.scrollTop
              )
            : 0,
          expandedToolGroups,
          expandedUserActionGroups,
          expandedToolUse,
        });
      }
      onSend(withSelectedChatModel(payload), () => {
        setDepartingConversation(null);
        setAnswerFiles(null);
        if (restoreOnError !== undefined) {
          const draft =
            composerContext?.restore(payload.text, restoreOnError) ??
            restoreOnError;
          restoreDraft(`${pathname}:${draftKey}`, draft);
          setInput((prev) => prev || draft);
        }
        requestAnimationFrame(scrollComposerToEnd);
      });
      resetScroll();
    },
    [
      onSend,
      composerContext,
      resetScroll,
      scrollComposerToEnd,
      setInput,
      pathname,
      draftKey,
      withSelectedChatModel,
      liveConversation,
      exploratoryConversation,
      exploring,
      sourceEntries,
      departingConversation,
      scrollContainerRef,
      expandedToolGroups,
      expandedUserActionGroups,
      expandedToolUse,
    ]
  );

  const releaseQueued = useCallback(
    (card: QueuedMessage, markFailed: () => void) => {
      const { id: _id, state: _s, ...payload } = card;
      playClick();
      onSend(withSelectedChatModel(payload), markFailed);
      resetScroll();
    },
    [onSend, withSelectedChatModel, resetScroll]
  );
  const [queue, setQueue] = useChatQueue(
    writeChatId,
    isActiveStatus || !canWrite || answerFiles !== null,
    editingId != null,
    releaseQueued
  );
  if (
    selectedQueueItem != null &&
    (input !== "" || selectedQueueItem.index >= queue.length)
  ) {
    setSelectedQueueItem(null);
  }
  if (editingId != null && !queue.some((x) => x.id === editingId)) {
    setEditingId(null);
  }

  const enqueueOrSend = useCallback(
    (payload: ChatSendPayload, restoreOnError?: string) => {
      if (isActiveStatus) {
        setQueue((q) => [
          ...q,
          { id: nanoid(), ...withSelectedChatModel(payload) },
        ]);
      } else {
        send(payload, restoreOnError);
      }
    },
    [isActiveStatus, setQueue, withSelectedChatModel, send]
  );

  useImperativeHandle(forwardedRef, () => ({
    enqueueOrSend,
  }));

  const forceSend = (card: QueuedMessage) => {
    setQueue((q) => q.filter((x) => x.id !== card.id));
    if (writeChatId && !claimQueued(writeChatId, card.id)) return;
    const { id: _id, state: _s, ...payload } = card;
    send(
      {
        ...payload,
        ...(canInterrupt(chatEntries) && {
          interruptEntryId: nanoid(12),
        }),
      },
      payload.text
    );
  };

  const cancelQueued = (id: string) => {
    setQueue((q) => q.filter((x) => x.id !== id));
  };

  const editQueued = (id: string, text: string) => {
    if (!text.trim()) return;
    setQueue((q) =>
      q.map((x) => (x.id === id ? { ...x, text, state: undefined } : x))
    );
  };

  const retryFromError = useCallback(
    (errorEntryId: string) => {
      const lastUser = latestUserMessageBefore(chatEntries, errorEntryId);
      const text = lastUser?.userMessage?.text;
      if (!text) return;
      const { planMode, effort } = composerDefaultsRef.current;
      enqueueOrSend(
        {
          text,
          isDictated: !!lastUser?.userMessage?.isDictated,
          planMode,
          effort: effort ?? undefined,
        },
        text
      );
    },
    [chatEntries, enqueueOrSend]
  );

  const hasStagedAttachments = pendingAttachments.length > 0;
  const canSendAttachments = hasStagedAttachments && !isUploading;

  const doSend = (force = false) => {
    if (isUploading) return;
    const payload = buildSendPayload({
      input,
      context: composerContext?.text,
      selectedStep: selectedStepName,
      planMode,
      effort,
      pendingAttachments,
      codeSnippets,
      elementSnippets,
      selectedSkills,
      selectedSteps,
      selectedConnectors,
    });
    if (!payload) return;

    const draftBeforeClear = input;
    if (force && isActiveStatus) {
      send(
        {
          ...payload,
          ...(canInterrupt(chatEntries) && {
            interruptEntryId: nanoid(12),
          }),
        },
        draftBeforeClear
      );
    } else {
      enqueueOrSend(payload, draftBeforeClear);
    }
    setInput("");
    requestAnimationFrame(updateComposerFade);
    clearAttachments();
    clearCodeSnippets?.();
    clearElementSnippets?.();
    composerContext?.clear();
    clearSkills();
    clearSteps();
    clearConnectors();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    doSend(resolveForce(false));
  };

  const autoResolvedTool = useMemo(() => {
    if (isResponding || writeChatId === null) return null;
    const lastTool = latestPendingToolUse(
      chatEntries,
      writeChatId,
      (toolName) =>
        CLIENT_AUTO_RESOLVED_TOOLS.has(toolName) &&
        !!toolsByName.get(toolName)?.PendingPrompt
    );
    if (!lastTool?.toolCall || lastTool.toolCall.callerUserId !== z.userID)
      return null;
    const tool = toolsByName.get(lastTool.toolCall.toolName);
    if (!tool?.PendingPrompt) return null;
    return {
      toolCallId: lastTool.id,
      input: lastTool.toolCall.input,
      PendingPrompt: tool.PendingPrompt,
    };
  }, [chatEntries, isResponding, toolsByName, writeChatId, z.userID]);

  const toolResultsInFlight = useRef(new Map<string, Promise<boolean>>());
  const handleProvideToolResult = useCallback(
    (
      entryId: string,
      output: unknown,
      isError: boolean,
      options?: { pauseAfter?: boolean }
    ) => {
      if (!writeChatId) return Promise.resolve(false);
      const pending = toolResultsInFlight.current.get(entryId);
      if (pending) return pending;
      cacheToolOutput(entryId, output);
      const result = onProvideToolResult(entryId, output, isError, {
        ...options,
        chatModel: composerDefaultsRef.current.effectiveChatModel ?? undefined,
      });
      toolResultsInFlight.current.set(entryId, result);
      const clear = () => toolResultsInFlight.current.delete(entryId);
      void result.then(clear, clear);
      resetScroll();
      return result;
    },
    [writeChatId, onProvideToolResult, resetScroll]
  );

  const pendingPromptNode = pendingPromptTool ? (
    <pendingPromptTool.PendingPrompt
      toolCallId={pendingPromptTool.toolCallId}
      input={pendingPromptTool.input}
      hasStagedAttachments={canSendAttachments}
      onResult={(output, isError, options) => {
        if (!writeChatId) return Promise.resolve(false);
        const { approvedPlan, sendAttachments, resultOptions } =
          resolvePromptResult(options, canSendAttachments);
        if (approvedPlan) {
          planModeTouchedRef.current = false;
          savePlanMode(writeChatId, false);
          setPlanMode(false);
        }
        const result = handleProvideToolResult(
          pendingPromptTool.toolCallId,
          output,
          isError,
          resultOptions
        );
        if (!sendAttachments) return result;
        const payload = {
          text: "",
          attachments: toAttachmentPayload(pendingAttachments),
          selectedStep: selectedStepName,
          planMode: approvedPlan ? false : planMode,
          effort: effort ?? undefined,
        };
        // Cleared before the save so a repeated submit or a composer send can’t send the same files twice.
        clearAttachments();
        setAnswerFiles("sending");
        // The answer pauses the run, so the files must land after it for their message to start the next turn with the answer in context.
        return result.then((saved) => {
          if (saved) send(payload);
          setAnswerFiles(saved ? "sent" : null);
          return saved;
        });
      }}
    />
  ) : null;

  const autoResolvedToolNode = autoResolvedTool ? (
    <autoResolvedTool.PendingPrompt
      toolCallId={autoResolvedTool.toolCallId}
      input={autoResolvedTool.input}
      hasStagedAttachments={false}
      onResult={(output, isError, options) =>
        handleProvideToolResult(
          autoResolvedTool.toolCallId,
          output,
          isError,
          options
        )
      }
    />
  ) : null;

  const handleSelectSuggestion = (prompt: string) => {
    send({ text: prompt, planMode, effort: effort ?? undefined }, prompt);
  };
  const chatSuggestions: SuggestionSet =
    reviewVersion && canWrite && isCurrentUserChat
      ? {
          ...suggestions,
          items: [
            {
              id: "review-changes",
              icon: "diffs",
              label: "Highlight changes",
              disabled:
                !reviewRequest.canGenerate || isActiveStatus || isResponding,
              run: () => void reviewRequest.generate(),
            },
            ...suggestions.items,
          ],
        }
      : suggestions;

  const currentTranscript = (
    <>
      {prevCheckpointLink && prevCheckpointLabel && (
        <PrevCheckpointLink to={prevCheckpointLink} data-visible={topVisible}>
          <Icon name="rotate-ccw" size={10} color="var(--text-secondary)" />
          <Text variant="text-small" color="var(--text-secondary)">
            {prevCheckpointLabel}
          </Text>
        </PrevCheckpointLink>
      )}
      <div data-chat-spacer />
      <TranscriptProvider
        value={{
          isGroupChat: groupChat,
          currentUserId: z.userID,
          toolsByName,
          isCurrentUserChat,
          canWrite,
          onProvideToolResult: handleProvideToolResult,
          onRetryFromError: retryFromError,
          onTypewriterAnimatingChange: handleTypewriterAnimatingChange,
          resolveFileHref,
        }}
        expansion={transcriptExpansion}
      >
        <Transcript
          reviewVersion={
            departingConversation && liveConversation
              ? undefined
              : reviewVersion
          }
          entries={chatEntries}
          groupedEntries={groupedEntries}
          streamingEntries={streamingEntries}
        />
      </TranscriptProvider>
      {nextCheckpointLink && nextCheckpointLabel && (
        <>
          <div ref={bottomSentinelRef} />
          <NextCheckpointLink
            to={nextCheckpointLink}
            data-visible={bottomVisible}
          >
            <Icon name="rotate-cw" size={10} color="var(--text-secondary)" />
            <Text variant="text-small" color="var(--text-secondary)">
              {nextCheckpointLabel}
            </Text>
          </NextCheckpointLink>
        </>
      )}
    </>
  );

  return (
    <Container data-outer-scroll={outerScroll || undefined}>
      <div
        ref={messagesWrapperRef}
        style={{ flex: 1, minHeight: 0, position: "relative" }}
      >
        <ConversationViewport
          previous={previousContext}
          currentScrollRef={scrollContainerRef}
          departing={departingConversation !== null}
          outerScroll={outerScroll}
          toolsByName={toolsByName}
          currentUserId={z.userID}
        >
          <Messages
            ref={outerScroll ? undefined : scrollContainerRef}
            data-current-conversation="true"
            data-exploratory={exploring}
            data-has-previous={!!previousContext}
            data-outer-scroll={outerScroll || undefined}
            onMouseDown={(e) => {
              if (
                entries.length === 0 &&
                !e.defaultPrevented &&
                !(e.target as HTMLElement).closest(
                  "[data-conversation-transcript]"
                )
              ) {
                e.preventDefault();
                textareaRef.current?.focus();
              }
            }}
            style={
              !isCurrentUserChat
                ? ({
                    "--chat-messages-bottom-padding": "16px",
                    "--chat-table-scroll-inset": outerScroll ? "0px" : "-16px",
                  } as React.CSSProperties)
                : queueOverlayHeight > 0
                  ? ({
                      "--chat-messages-bottom-padding": `${56 + queueOverlayHeight + 12}px`,
                    } as React.CSSProperties)
                  : undefined
            }
          >
            <MessagesContent data-outer-scroll={outerScroll || undefined}>
              {loadingOlder && <HistoryLoading />}
              <div
                ref={topSentinelRef}
                style={{ height: 0, overflow: "hidden", flexShrink: 0 }}
              />
              {exploratoryConversation?.banner && conversationToDisplay && (
                <FlowingLiveConversation
                  key={exploratoryConversation.live.id}
                  detached={
                    !!departingConversation?.reviewVersion &&
                    departingConversation.reviewVersion.commitId !==
                      exploratoryConversation.live.reviewVersion?.commitId
                  }
                  conversation={conversationToDisplay}
                  animate={departingConversation !== null}
                  onCloseComplete={exploratoryConversation.onCloseComplete}
                  onVisibilityChange={setLivePanelVisible}
                  toolsByName={toolsByName}
                  currentUserId={z.userID}
                  banner={
                    <ChatBanner
                      {...exploratoryConversation.banner}
                      placement="inline"
                    />
                  }
                />
              )}
              {hasExplored ? (
                <section
                  ref={transcriptRef}
                  aria-label={exploring ? "New chat" : undefined}
                  style={{
                    marginTop: exploring || outerScroll ? undefined : "auto",
                    display: "flex",
                    flexDirection: "column",
                    flexShrink: 0,
                  }}
                >
                  {currentTranscript}
                </section>
              ) : (
                currentTranscript
              )}
            </MessagesContent>
          </Messages>
          {!outerScroll && (
            <ChatMessageNav
              entries={chatEntries}
              hidden={
                returningToLive ||
                (!!exploratoryConversation?.banner && livePanelVisible)
              }
              scrollContainer={scrollContainerRef}
              wrapperRef={messagesWrapperRef}
            />
          )}
          {isCurrentUserChat && canWrite && (
            <QueueStack
              queue={queue}
              overlayRef={setQueueOverlayRef}
              onForceSend={forceSend}
              onCancel={cancelQueued}
              scrollContainer={scrollContainerRef}
              selectedIndex={selectedQueueItem?.index ?? null}
              selectedButton={selectedQueueItem?.button ?? null}
              editingId={editingId}
              onEditStart={(id) => setEditingId(id)}
              onEditCommit={(id, text) => {
                editQueued(id, text);
                setEditingId(null);
                textareaRef.current?.focus();
              }}
              onEditCancel={() => {
                setEditingId(null);
                textareaRef.current?.focus();
              }}
            />
          )}
        </ConversationViewport>
        {topOverlay}
        {isCurrentUserChat && entries.length === 0 && EmptyState && (
          <EmptyState fillInput={fillInput} visible={input === ""} />
        )}
      </div>

      {isCurrentUserChat && (
        <ChatForm
          ref={setComposerRef}
          onSubmit={handleSubmit}
          data-outer-scroll={outerScroll || undefined}
        >
          <TextareaContainer
            style={{
              width: "100%",
              ...{
                padding: 4,
                margin: -4.5,
                background:
                  "linear-gradient(var(--alpha2), var(--alpha2)) padding-box, light-dark(var(--surface-secondary), var(--surface-primary))",
                border: "0.5px solid var(--border-alpha)",
              },
            }}
          >
            {isScrolledUp && !returningToLive && (
              <ScrollToBottomButton
                aria-label="Scroll to bottom"
                onPress={scrollToBottom}
                style={
                  queueOverlayHeight > 0
                    ? {
                        transform: `translate(-50%, calc(-100% - 12px - ${queueOverlayHeight + 12}px))`,
                      }
                    : undefined
                }
              >
                <Icon name="arrow-down" size={12} />
              </ScrollToBottomButton>
            )}
            <StatusContainer>
              {onStatusClick ? (
                <FollowNudge
                  show={showFollowNudge}
                  trigger={({ style }) => (
                    <span style={{ ...style, display: "inline-flex" }}>
                      <Tooltip
                        content={
                          followActive ? "Stop following 3B" : "Follow 3B"
                        }
                        hidden={showFollowNudge}
                      >
                        <FollowAvatarButton
                          onClick={(e) => {
                            e.stopPropagation();
                            onDismissFollowNudge?.();
                            onStatusClick();
                          }}
                          aria-label={
                            followActive ? "Stop following 3B" : "Follow 3B"
                          }
                        >
                          <StatusIndicator
                            state={isDragging ? "dragging" : displayStatusState}
                            focused={
                              isDragging || isTextareaFocused || isRecording
                            }
                            planMode={planMode}
                            celebrateTrigger={celebrateTrigger}
                          />
                        </FollowAvatarButton>
                      </Tooltip>
                    </span>
                  )}
                />
              ) : (
                <StatusIndicator
                  state={isDragging ? "dragging" : displayStatusState}
                  focused={isDragging || isTextareaFocused || isRecording}
                  planMode={planMode}
                  celebrateTrigger={celebrateTrigger}
                />
              )}
              <Text
                variant="text-small-md"
                ellipsis
                color="var(--text-primary)"
                style={{
                  opacity:
                    isDragging ||
                    isRecording ||
                    isActiveStatus ||
                    reviewing ||
                    isTextareaFocused ||
                    planMode
                      ? 1
                      : 0.2,
                }}
              >
                {isDragging ? (
                  statusText.dragging
                ) : isRecording ? (
                  "Listening"
                ) : reviewing ? (
                  "3B is reviewing"
                ) : (
                  <StatusLabel
                    statusState={statusState}
                    respondingAt={respondingAt}
                    lastLoopHeartbeatAt={lastLoopHeartbeatAt}
                    planMode={planMode}
                  />
                )}
              </Text>
              {(isActiveStatus || reviewing) &&
                !isRecording &&
                activityStartedAt && (
                  <StatusTimer respondingAt={activityStartedAt} />
                )}
              {isRecording && (
                <RecordingIndicator>
                  <RecordingDot />
                  <Text variant="text-small-md" color="var(--text-secondary)">
                    {formatElapsedTime(recordingElapsedTime)}
                  </Text>
                </RecordingIndicator>
              )}
            </StatusContainer>

            <AttachmentRowAnimator
              visible={
                !!composerContext?.pill ||
                pendingAttachments.length > 0 ||
                (codeSnippets ?? []).length > 0 ||
                (elementSnippets ?? []).length > 0 ||
                selectedSkills.length > 0 ||
                selectedSteps.length > 0 ||
                selectedConnectors.length > 0
              }
            >
              <AttachmentPreviewRow>
                {composerContext?.pill}
                {selectedSkills.map((skill) => (
                  <AttachmentPreview
                    key={skill.id}
                    onPress={() => removeSkill(skill.id)}
                  >
                    <span className="file-icon">
                      <Icon name="skills" size={10} />
                    </span>
                    <span className="remove-icon">
                      <Icon name="x" size={10} />
                    </span>
                    <Text variant="text-small-md" ellipsis>
                      {skill.name}
                    </Text>
                  </AttachmentPreview>
                ))}
                {selectedSteps.map((step) => (
                  <AttachmentPreview
                    key={step.name}
                    onPress={() => removeStep(step.name)}
                  >
                    <span className="file-icon">
                      <AccentBar
                        style={{ color: `var(--${step.color}-600)` }}
                      />
                    </span>
                    <span className="remove-icon">
                      <Icon name="x" size={10} />
                    </span>
                    <Text variant="text-small-md" ellipsis>
                      {step.name}
                    </Text>
                  </AttachmentPreview>
                ))}
                {selectedConnectors.map((connector) => (
                  <AttachmentPreview
                    key={connector.name}
                    onPress={() => removeConnector(connector.name)}
                  >
                    <span className="file-icon">
                      <ConnectorLogo
                        template={{
                          name: connector.name,
                          identifier: connector.identifier,
                        }}
                        size={10}
                      />
                    </span>
                    <span className="remove-icon">
                      <Icon name="x" size={10} />
                    </span>
                    <Text variant="text-small-md" ellipsis>
                      {connector.name}
                    </Text>
                  </AttachmentPreview>
                ))}
                {pendingAttachments.map((att) => (
                  <AttachmentHoverPreview
                    key={att.id}
                    fileName={att.fileName}
                    mediaType={att.mediaType}
                    sizeBytes={att.sizeBytes}
                    url={att.previewUrl}
                    onRemove={() => removeAttachment(att.id)}
                  >
                    <AttachmentPreview onPress={() => removeAttachment(att.id)}>
                      <span className="file-icon">
                        {att.remoteId ? (
                          <Icon
                            name={attachmentFileIcon(att.mediaType)}
                            size={10}
                          />
                        ) : (
                          <Spinner size={10} />
                        )}
                      </span>
                      <span className="remove-icon">
                        <Icon name="x" size={10} />
                      </span>
                      <Text variant="text-small-md" ellipsis>
                        {att.fileName}
                      </Text>
                    </AttachmentPreview>
                  </AttachmentHoverPreview>
                ))}
                {(codeSnippets ?? []).map((snippet) => (
                  <AttachmentPreview
                    key={snippet.id}
                    onPress={() => removeCodeSnippet?.(snippet.id)}
                  >
                    <span className="file-icon">
                      <AccentBar
                        style={{
                          color: `var(--${snippet.color}-600)`,
                        }}
                      />
                    </span>
                    <span className="remove-icon">
                      <Icon name="x" size={10} />
                    </span>
                    <Text variant="text-small-md" ellipsis>
                      {displaySnippetPath(snippet.filePath)}:{" "}
                      {snippet.fromLine === snippet.toLine
                        ? snippet.fromLine
                        : `${snippet.fromLine}–${snippet.toLine}`}
                    </Text>
                  </AttachmentPreview>
                ))}
                {(elementSnippets ?? []).map((snippet) => (
                  <AttachmentPreview
                    key={snippet.id}
                    onPress={() => removeElementSnippet?.(snippet.id)}
                    onMouseEnter={() =>
                      window.dispatchEvent(
                        new CustomEvent("3b-highlight-element", {
                          detail: {
                            index: snippet.index,
                            sourceId: snippet.sourceId,
                          },
                        })
                      )
                    }
                    onMouseLeave={() =>
                      window.dispatchEvent(
                        new CustomEvent("3b-highlight-element", {
                          detail: { index: null, sourceId: snippet.sourceId },
                        })
                      )
                    }
                  >
                    <span
                      className="file-icon"
                      style={{
                        alignItems: "center",
                        justifyContent: "center",
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        background: `var(--${snippet.color}-600)`,
                        color: "white",
                        fontSize: 9,
                        fontWeight: 500,
                        flexShrink: 0,
                        marginLeft: -3,
                      }}
                    >
                      {snippet.index}
                    </span>
                    <span
                      className="remove-icon"
                      style={{
                        alignItems: "center",
                        justifyContent: "center",
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        background: `var(--${snippet.color}-600)`,
                        flexShrink: 0,
                        marginLeft: -3,
                      }}
                    >
                      <Icon name="x" size={10} color="white" />
                    </span>
                    <Text variant="text-small-md" ellipsis>
                      {snippet.label || snippet.descriptor}
                    </Text>
                  </AttachmentPreview>
                ))}
              </AttachmentPreviewRow>
            </AttachmentRowAnimator>

            {autoResolvedToolNode}
            {pendingPromptOverride ? (
              pendingPromptOverride
            ) : pendingPromptNode ? (
              pendingPromptNode
            ) : showSuggestions ? (
              <SlashMenu
                onSelect={handleSelectSuggestion}
                onSelectSkill={(skill) => {
                  addSkill(skill);
                  requestAnimationFrame(() => textareaRef.current?.focus());
                }}
                onDismiss={() => setShowSuggestions(false)}
                suggestions={chatSuggestions}
                skills={availableSkills}
                canCreateSkill={canCreateSkill}
                onCreateSkill={() => {
                  const id = createSkill();
                  if (id) navigate(routes.modal.skill, { id: id });
                }}
              />
            ) : isRecording ? (
              <VoiceRecorderContainer>
                <VoiceRecorder
                  leftControls={
                    planModeEnabled ? (
                      <PlanModeToggle
                        planMode={planMode}
                        onToggle={togglePlanMode}
                      />
                    ) : undefined
                  }
                  onTranscript={(text) => {
                    const trimmed = text.trim();
                    setInput(trimmed);
                    requestAnimationFrame(() => {
                      const el = textareaRef.current;
                      if (el) {
                        el.focus();
                        el.setSelectionRange(trimmed.length, trimmed.length);
                      }
                      scrollComposerToEnd();
                    });
                  }}
                  onCancel={() => {
                    setIsRecording(false);
                    setRecordingStartTime(null);
                  }}
                  onSend={(text) => {
                    setIsRecording(false);
                    setRecordingStartTime(null);
                    if (isUploading) {
                      setInput(text);
                      return;
                    }
                    const payload = buildSendPayload({
                      input: text,
                      context: composerContext?.text,
                      planMode,
                      effort,
                      pendingAttachments,
                      selectedSkills: [],
                      selectedSteps: [],
                      selectedConnectors: [],
                    });
                    if (!payload) return;
                    enqueueOrSend({ ...payload, isDictated: true }, text);
                    setInput("");
                    requestAnimationFrame(updateComposerFade);
                    clearAttachments();
                    composerContext?.clear();
                  }}
                />
              </VoiceRecorderContainer>
            ) : (
              <ComposerInputWrap
                data-composer
                data-show-fade={showComposerFade || undefined}
              >
                <Textarea
                  autoFocus={!isMobile}
                  disabled={!canWrite}
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => {
                    const shouldFollowCaret =
                      e.target.selectionStart === e.target.value.length &&
                      e.target.selectionEnd === e.target.value.length;
                    setInput(e.target.value);
                    syncSlashToken(e.target);
                    syncMentionToken(e.target);
                    requestAnimationFrame(
                      shouldFollowCaret
                        ? scrollComposerToEnd
                        : updateComposerFade
                    );
                  }}
                  onKeyUp={(e) => {
                    syncSlashToken(e.currentTarget);
                    syncMentionToken(e.currentTarget);
                  }}
                  onClick={(e) => {
                    syncSlashToken(e.currentTarget);
                    syncMentionToken(e.currentTarget);
                  }}
                  onSelect={(e) => {
                    syncSlashToken(e.currentTarget);
                    syncMentionToken(e.currentTarget);
                  }}
                  onScroll={updateComposerFade}
                  placeholder={
                    isDragging
                      ? ""
                      : `${placeholder}, or type / for skills and prompts…`
                  }
                  onFocus={() => setIsTextareaFocused(true)}
                  onBlur={() => setIsTextareaFocused(false)}
                  onPaste={handlePaste}
                  onKeyDown={(e) => {
                    if (handleSlashKeyDown(e)) return;
                    if (handleMentionKeyDown(e)) return;
                    if (
                      e.key === "/" &&
                      !e.metaKey &&
                      !e.ctrlKey &&
                      input === ""
                    ) {
                      e.preventDefault();
                      setShowSuggestions(true);
                      return;
                    }
                    if (input === "" && queue.length > 0) {
                      if (e.key === "ArrowUp") {
                        e.preventDefault();
                        setSelectedQueueItem((prev) => ({
                          index:
                            prev == null
                              ? queue.length - 1
                              : Math.max(0, prev.index - 1),
                          button: prev?.button ?? "send",
                        }));
                        return;
                      }
                      if (e.key === "ArrowDown" && selectedQueueItem != null) {
                        e.preventDefault();
                        if (selectedQueueItem.index >= queue.length - 1) {
                          setSelectedQueueItem(null);
                        } else {
                          setSelectedQueueItem({
                            ...selectedQueueItem,
                            index: selectedQueueItem.index + 1,
                          });
                        }
                        return;
                      }
                      if (selectedQueueItem != null) {
                        if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
                          e.preventDefault();
                          const order = ["cancel", "edit", "send"] as const;
                          const delta = e.key === "ArrowLeft" ? -1 : 1;
                          const idx = Math.min(
                            order.length - 1,
                            Math.max(
                              0,
                              order.indexOf(selectedQueueItem.button) + delta
                            )
                          );
                          setSelectedQueueItem({
                            ...selectedQueueItem,
                            button: order[idx]!,
                          });
                          return;
                        }
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const card = queue[selectedQueueItem.index];
                          if (card) {
                            playClick();
                            if (selectedQueueItem.button === "send")
                              forceSend(card);
                            else if (selectedQueueItem.button === "edit")
                              setEditingId(card.id);
                            else cancelQueued(card.id);
                          }
                          setSelectedQueueItem(null);
                          return;
                        }
                        if (e.key === "Escape") {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedQueueItem(null);
                          return;
                        }
                      }
                    }
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      doSend(resolveForce(e.metaKey || e.ctrlKey));
                    }
                    if (e.key === "Escape") {
                      e.currentTarget.blur();
                      e.stopPropagation();
                    }
                  }}
                  minRows={4}
                  maxRows={20}
                  style={{
                    minHeight: 96,
                    paddingRight: 12,
                    paddingBottom: 44,
                    ...(isDragging && { color: "transparent" }),
                    // While a completion shows, the overlay renders the text so the ghost can push what follows the caret; the textarea keeps only the caret.
                    ...((slashCompletion.suffix !== "" ||
                      mentionCompletion.suffix !== "") && {
                      color: "transparent",
                      caretColor: "var(--text-primary)",
                    }),
                  }}
                />
                <SlashSkillCompletion
                  textareaRef={textareaRef}
                  value={input}
                  isTextareaFocused={isTextareaFocused}
                  completion={slashCompletion}
                />
                <MentionCompletion
                  textareaRef={textareaRef}
                  value={input}
                  isTextareaFocused={isTextareaFocused}
                  completion={mentionCompletion}
                />
                {!isDragging && (
                  <ComposerControls>
                    {planModeEnabled && (
                      <ComposerControlGroup>
                        <PlanModeToggle
                          planMode={planMode}
                          onToggle={togglePlanMode}
                        />
                      </ComposerControlGroup>
                    )}
                    <ComposerStripToggle />
                    <ComposerControlGroup>
                      <VoiceModeButton
                        onClick={() => {
                          setIsRecording(true);
                          setRecordingStartTime(Date.now());
                          setRecordingElapsedTime(0);
                        }}
                        disabled={!canWrite}
                      />
                      {isActiveStatus && writeChatId && !input.trim() ? (
                        <Tooltip content="Stop">
                          <StopButton
                            type="button"
                            icon="stop"
                            variant="secondary"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              if (canInterrupt(chatEntries)) {
                                onInterrupt();
                              }
                            }}
                            disabled={!canWrite}
                          />
                        </Tooltip>
                      ) : (
                        <Button
                          icon="arrow-up"
                          type="button"
                          onClick={(e) =>
                            doSend(resolveForce(e.metaKey || e.ctrlKey))
                          }
                          disabled={
                            isUploading ||
                            !hasSendableContent({
                              input,
                              pendingAttachments,
                              codeSnippets,
                              elementSnippets,
                              selectedSkills,
                              selectedSteps,
                              selectedConnectors,
                            })
                          }
                        />
                      )}
                    </ComposerControlGroup>
                  </ComposerControls>
                )}
                {isDragging && (
                  <Dropzone
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => e.preventDefault()}
                  >
                    <svg
                      width="100%"
                      height="100%"
                      style={{ position: "absolute", inset: 0 }}
                    >
                      <rect
                        x="1"
                        y="1"
                        width="calc(100% - 2px)"
                        height="calc(100% - 2px)"
                        rx="12"
                        ry="12"
                        fill="none"
                        stroke="var(--text-primary)"
                        strokeWidth="1"
                        strokeDasharray="6 4"
                        style={{
                          animation: `${marchingDashes} 0.5s linear infinite`,
                        }}
                      />
                    </svg>
                    <Icon
                      name="arrow-down"
                      size={10}
                      color="var(--text-primary)"
                    />
                    <Text variant="text-small" color="var(--text-primary)">
                      Drop files here
                    </Text>
                  </Dropzone>
                )}
              </ComposerInputWrap>
            )}
            <ComposerStrip
              onAddFiles={processFiles}
              modelScope={modelScope}
              effort={effort}
              onEffortChange={(next) => {
                saveEffort(writeChatId, next);
                setLocalEffort(next);
              }}
              chatModel={effectiveChatModel}
              onChatModelChange={setChatModel}
            />
          </TextareaContainer>
        </ChatForm>
      )}
      {!isCurrentUserChat && historyBanner && (
        <div style={{ padding: "0 4px 4px", margin: "-0.5px" }}>
          {historyBanner}
        </div>
      )}
      {flyingFiles.map((f) => (
        <FlyingDot
          key={f.id}
          startX={f.x}
          startY={f.y}
          onDone={() => removeFlyingFile(f.id)}
        />
      ))}
    </Container>
  );
});
